import type { Aggregate } from 'edit/Aggregate';
import { Property } from 'edit/Property';
import type { OutputMatcher, OutputMatcherFile, OutputMatcherProceed } from 'api/node_definitions/endpoints/types';
import type { EditableNodeDefinition } from './EditableNodeDefinition';

/**
 * How a matched pattern supplies `new_exit_code` on the wire. The wire has no Success/Fail enum — only a nullable integer
 * that the backend maps to status (`0` → completed, non-zero → failed, `null` → leave the agent's exit/status alone). The UI
 * splits that into Success (always exit 0, field disabled and showing 0) vs Fail (editable: empty or any non-zero integer —
 * `0` is not a Fail value because the backend treats it as completed).
 */
export type OverrideStatus = 'success' | 'fail';

const DEFAULT_OUTPUT_FILE: OutputMatcherFile = 'stdout.log';
const DEFAULT_OVERRIDE_STATUS: OverrideStatus = 'fail';
const DEFAULT_PROCEED: OutputMatcherProceed = 'continue';

// Monotonic source of stable per-instance keys for React list rendering / drag ids. New matchers have no server id, and array
// index is unstable across add/remove/reorder, so each instance gets a unique key at construction that never changes.
let nextOutputMatcherKey = 0;

/**
 * One output matcher, as a reactive child of an `EditableNodeDefinition`. The parent→child relationship is owned by a shared
 * `Aggregate` that both sides hold; this child routes its self-delete through it. Every user-fillable field is a `Property`.
 *
 * The Fail return code is kept even while Success is selected (the form shows 0 and disables the field, without clearing the
 * Fail code) so toggling Success↔Fail doesn't lose a typed code. `toSave` reads only what the chosen override implies.
 * Entering `0` in Fail mode switches to Success, matching backend status mapping.
 */
export class EditableOutputMatcher {
  private readonly _aggregate: Aggregate<EditableNodeDefinition, EditableOutputMatcher>;
  private readonly _caseInsensitive = new Property<boolean>(false);
  /** Return code shown/edited for Fail (`null` or non-zero). Not used while Success is selected (UI shows 0). */
  private readonly _customReturnCode = new Property<number | null>(null);
  /** Stable identity for React list keys / drag ids; never changes after construction. */
  readonly key = `output-matcher-${(nextOutputMatcherKey += 1)}`;
  private readonly _outputFile = new Property<OutputMatcherFile>(DEFAULT_OUTPUT_FILE);
  private readonly _overrideStatus = new Property<OverrideStatus>(DEFAULT_OVERRIDE_STATUS);
  private readonly _proceed = new Property<OutputMatcherProceed>(DEFAULT_PROCEED);
  private readonly _searchString = new Property<string | null>(null);
  private readonly _useRegex = new Property<boolean>(false);

  /**
   * @param aggregate the shared parent→children owner; self-delete routes through it.
   * @param wire when present, seeds the fields from a loaded matcher; when absent, the field defaults stand (a fresh Fail
   *   matcher with an empty return code = keep agent exit/status).
   */
  constructor(aggregate: Aggregate<EditableNodeDefinition, EditableOutputMatcher>, wire?: OutputMatcher) {
    this._aggregate = aggregate;
    if (wire !== undefined) {
      this._outputFile.set(wire.output_file);
      this._searchString.set(wire.search_string);
      this._caseInsensitive.set(wire.case_insensitive ?? false);
      this._useRegex.set(wire.use_regex ?? false);
      this._proceed.set(wire.proceed ?? DEFAULT_PROCEED);
      if (wire.new_exit_code === 0) {
        this._overrideStatus.set('success');
      } else {
        // Non-zero or null -> Fail mode. Null means keep the agent's exit/status on match.
        this._overrideStatus.set('fail');
        this._customReturnCode.set(wire.new_exit_code ?? null);
      }
    }
  }

  get caseInsensitive(): boolean {
    return this._caseInsensitive.get();
  }
  setCaseInsensitive = this._caseInsensitive.set;

  /**
   * Value shown in the return-code field. Success always displays 0 (field is disabled); Fail shows the typed code
   * (`null` or non-zero — never 0 in Fail mode).
   */
  get customReturnCode(): number | null {
    return this._overrideStatus.get() === 'success' ? 0 : this._customReturnCode.get();
  }

  /**
   * Sets the Fail return code. Empty (`null`) and any non-zero integer are kept in Fail mode. `0` switches to Success
   * because the backend maps exit 0 to completed.
   */
  setCustomReturnCode = (value: number | null): void => {
    if (value === 0) {
      this._overrideStatus.set('success');
      return;
    }
    this._customReturnCode.set(value);
  };

  get outputFile(): OutputMatcherFile {
    return this._outputFile.get();
  }
  setOutputFile = this._outputFile.set;

  get overrideStatus(): OverrideStatus {
    return this._overrideStatus.get();
  }
  setOverrideStatus = this._overrideStatus.set;

  get proceed(): OutputMatcherProceed {
    return this._proceed.get();
  }
  setProceed = this._proceed.set;

  get searchString(): string | null {
    return this._searchString.get();
  }
  setSearchString = this._searchString.set;

  /**
   * A one-line plain-language summary of the matcher, or `null` when there's no search string yet. Presentational — the tab
   * renders it as the card subtitle.
   */
  get summary(): string | null {
    const search = this._searchString.get();
    if (!search) return null;
    const code = this._customReturnCode.get();
    const outcome = this._overrideStatus.get() === 'fail' ? (code !== null ? `Fail (${code})` : 'Fail') : 'Success';
    return `${outcome} when ${this._outputFile.get()} contains "${search}"`;
  }

  get useRegex(): boolean {
    return this._useRegex.get();
  }
  setUseRegex = this._useRegex.set;

  /** Remove this matcher from its definition. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  /**
   * Project to the wire shape. Success -> exit 0; Fail -> the typed return code (`null` keeps agent exit/status; non-zero
   * only — Fail never persists 0). `sort_order` is omitted — array position conveys order.
   */
  toSave(): OutputMatcher {
    if (this._overrideStatus.get() === 'success') {
      return {
        output_file: this._outputFile.get(),
        search_string: this._searchString.get() ?? '',
        case_insensitive: this._caseInsensitive.get(),
        use_regex: this._useRegex.get(),
        new_exit_code: 0,
        proceed: this._proceed.get(),
      };
    }
    const code = this._customReturnCode.get();
    return {
      output_file: this._outputFile.get(),
      search_string: this._searchString.get() ?? '',
      case_insensitive: this._caseInsensitive.get(),
      use_regex: this._useRegex.get(),
      // Defensive: never send 0 from Fail mode (would be completed on the backend).
      new_exit_code: code === 0 ? null : code,
      proceed: this._proceed.get(),
    };
  }
}
